import cn from 'classnames';
import React from 'react';

import { useSelector } from 'editor/src/store/hooks';
import getQuantitySavingsQuote from 'editor/src/store/quantitySavings/selector/getQuantitySavingsQuote';
import useIsQuantitySavingsPending from 'editor/src/store/quantitySavings/useIsQuantitySavingsPending';
import getNextQuantitySavingsTier from 'editor/src/store/quantitySavings/util/getNextQuantitySavingsTier';

import useTr from 'editor/src/util/useTr';

import IconChevronUp from 'editor/src/component/Icon/IconChevronUp';

import styles from './index.module.scss';

interface Props {
  compact?: boolean;
}

/**
 * The header price while the host quotes quantity price breaks: unit price, total and saving side by
 * side. Never `N × unit = total` — the host rounds the line total once, the way checkout charges it,
 * so the product of a rounded unit price and the quantity can be a cent off.
 */
function QuantitySavingsPrice({ compact }: Props) {
  const tr = useTr();
  const quote = useSelector(getQuantitySavingsQuote);
  const isPending = useIsQuantitySavingsPending();
  if (!quote) {
    return null;
  }

  const next = getNextQuantitySavingsTier(quote);
  const { quantity } = quote;
  const itemsText =
    quantity === 1
      ? tr('editor-quantity-savings-items-one', '1 item')
      : tr('editor-quantity-savings-items', '{{count}} items', { count: quantity });

  if (compact) {
    return (
      <div
        className={cn(styles.QuantitySavingsPrice, styles.compact, 'cy-quantity-savings-price')}
        aria-busy={isPending}
      >
        <span className={cn(styles.total, 'cy-quantity-savings-total')}>{quote.totalText}</span>
        {quote.savingsText && (
          <span className={cn(styles.savings, 'cy-quantity-savings-savings')}>
            {tr('editor-quantity-savings-save', 'Save {{amount}}', { amount: quote.savingsText })}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className={cn(styles.QuantitySavingsPrice, 'cy-quantity-savings-price')} aria-busy={isPending}>
      {next && (
        <div className={cn(styles.nudge, 'cy-quantity-savings-nudge')}>
          <IconChevronUp className={styles.nudgeIcon} />
          {tr('editor-quantity-savings-nudge-saving', 'Add {{count}} more to save {{saving}}', {
            count: next.additionalQuantity,
            saving: next.tier.savingText,
          })}
        </div>
      )}
      <div className={styles.figures}>
        <div className={styles.unitLine}>
          {quote.baseUnitPriceText && (
            <span className={cn(styles.baseUnit, 'cy-quantity-savings-base-unit')}>{quote.baseUnitPriceText}</span>
          )}
          <span className={cn(styles.unit, 'cy-quantity-savings-unit')}>{quote.unitPriceText}</span>
          {quantity > 1 && <span className={styles.each}>{tr('editor-quantity-savings-each', 'each')}</span>}
        </div>
        <div className={styles.totalLine}>
          <span>
            {itemsText}
            {quantity > 1 && (
              <>
                {` · ${tr('editor-quantity-savings-total', 'Total')} `}
                <strong className="cy-quantity-savings-total">{quote.totalText}</strong>
              </>
            )}
          </span>
          {quote.savingsText && (
            <span className={cn(styles.savings, 'cy-quantity-savings-savings')}>
              {tr('editor-quantity-savings-you-save', 'You save {{amount}}', { amount: quote.savingsText })}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

export default React.memo(QuantitySavingsPrice);
